import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import api, { apiMediaUrl } from '../api/api';
import { Volume2, Loader2, CheckCircle, RotateCcw, ArrowRight, SkipForward, Info, ChevronDown, X } from 'lucide-react';
import RecordButton from '../components/mockInterview/RecordButton';
import { pickRecorderMimeType, extensionForMimeType } from '../utils/audioRecording';

// audioURL is a path relative to the API (streamed through our own backend, see apiMediaUrl in
// api.js), not a direct Blob Storage link. isPending === false means the question already has a
// submitted response (resumed session) — UI skips its recording flow.
async function fetchSessionData(sessionId) {
  try {
    const res = await api.get(`/interview/sessions/${sessionId}/start`);
    return { ...res.data, questions: res.data?.questionList || [] };
  } catch (err) {
    console.log("Error getting Session data", err)
    throw err;
  }
}

async function submitResponseAudio(responseId, audioBlob) {
  try {
    // Extension must match the blob's real recorded type (see startRecording) — Safari
    // records MP4, not WebM, and mislabeling it breaks playback later.
    const ext = extensionForMimeType(audioBlob.type);
    const formData = new FormData();
    formData.append('audio', audioBlob, `response-${responseId}.${ext}`)
    // `api` defaults to application/json, which makes axios JSON-stringify FormData and drop the audio
    // bytes — unset it so the browser sets multipart/form-data with the boundary.
    const res = await api.post(`/interview/responses/${responseId}/audio`, formData, {
      headers: { 'Content-Type': undefined },
    })
    return res.data
  } catch (err) {
    console.log("Error submitting audio response", err)
    throw err;
  }
}

async function endSession(sessionId) {
  // Let the final audio upload settle on the backend before signalling it to start analysis.
  await new Promise((resolve) => setTimeout(resolve, 400));

  try {
    const res = await api.post(`/interview/sessions/${sessionId}/end`);

    // This page runs in its own tab — ping the MockInterview tab so its History list re-fetches.
    // Best-effort: if no History tab is open, nothing listens.
    try {
      const channel = new BroadcastChannel('interview-sessions');
      channel.postMessage({ type: 'session-ended', sessionId });
      channel.close();
    } catch (broadcastErr) {
      console.log("Could not broadcast session-ended", broadcastErr);
    }

    return res.data;
  } catch (err) {
    console.log("Error ending session", err);
    throw err;
  } finally {
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
}

async function skipResponse(responseId) {
  try {
    const res = await api.post(`interview/responses/${responseId}/skip`);
    return res.data;
  } catch (err) {
    console.log("Error skipping response", err);
    throw err;
  }
}

// Hard cap on a single answer recording. Recording auto-stops when this is reached.
const MAX_RECORDING_SECONDS = 180; // 3 minutes

function formatDuration(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const CAT_COLORS = {
  behavioral: { light: 'bg-primary-100 text-primary-700', dark: 'bg-primary-400/10 text-primary-400' },
  'role-specific': { light: 'bg-yellow-100 text-yellow-700', dark: 'bg-yellow-400/10 text-yellow-400' },
  leadership: { light: 'bg-red-100 text-red-700', dark: 'bg-red-400/10 text-red-400' },
};

const CATEGORY_LABELS = {
  behavioral: 'Behavioral',
  'role-specific': 'Role-specific',
  leadership: 'Leadership',
};

function CategoryBadge({ category, isDarkMode }) {
  const colorClass = isDarkMode ? CAT_COLORS[category]?.dark : CAT_COLORS[category]?.light;
  return (
    <span className={`text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${colorClass || ''}`}>
      {CATEGORY_LABELS[category] || category}
    </span>
  );
}

export default function InterviewSession() {
  const { user } = useAuth(); // eslint-disable-line no-unused-vars -- not displayed yet, kept for when it is
  const { isDarkMode } = useTheme();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [sessionData, setSessionData] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // recording state: 'idle' | 'recording' | 'recorded' | 'uploading'
  const [recordingState, setRecordingState] = useState('idle');
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [finished, setFinished] = useState(false);
  const [proceeding, setProceeding] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const questionAudioRef = useRef(null);
  const recordTimerRef = useRef(null);
  const recordStartRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setLoadError('No session specified — this page should be opened from the mock interview setup screen.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchSessionData(sessionId)
      .then((data) => {
        if (cancelled) return;
        setSessionData(data);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError('Could not load this interview session. Try again.');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [sessionId]);

  const questions = sessionData?.questions || [];
  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  // isPending === false (not just falsy) — question already answered in a resumed session.
  const alreadyAnswered = currentQuestion?.isPending === false;

  // Plays the question's pre-generated audio, streamed through our own backend (see
  // apiMediaUrl) rather than a direct Blob Storage link — can still fail on network/expired session.
  const speakQuestion = useCallback(() => {
    if (!currentQuestion?.audioURL) return;

    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current = null;
    }

    const player = new Audio(apiMediaUrl(currentQuestion.audioURL));
    questionAudioRef.current = player;
    player.onplay = () => setIsSpeaking(true);
    player.onended = () => setIsSpeaking(false);
    player.onerror = () => {
      console.error('Could not load question audio (network/expired session?):', currentQuestion.audioURL);
      setIsSpeaking(false);
    };
    player.play().catch((err) => {
      console.error('Could not play question audio:', err);
      setIsSpeaking(false);
    });
  }, [currentQuestion]);

  // Auto-read each new question, except already-answered ones (resumed-session pass-through).
  useEffect(() => {
    if (currentQuestion && currentQuestion.isPending !== false) speakQuestion();
    return () => {
      questionAudioRef.current?.pause();
      questionAudioRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, sessionData]);

  // Auto-expand tips on question 1, auto-collapse after. The rAF defers the open by one frame so the
  // panel (not mounted while `loading`) paints collapsed first and the CSS transition actually runs.
  useEffect(() => {
    if (loading) return;
    if (currentIndex !== 0) {
      setTipsOpen(false);
      return;
    }
    const id = requestAnimationFrame(() => setTipsOpen(true));
    return () => cancelAnimationFrame(id);
  }, [currentIndex, loading]);

  const clearRecordTimers = () => {
    if (recordTimerRef.current) { clearInterval(recordTimerRef.current); recordTimerRef.current = null; }
  };

  const startRecording = async () => {
    setSubmitError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = pickRecorderMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        // recorder.mimeType is what the browser actually used — Safari ignores webm and
        // records mp4/aac regardless of what we asked for, so read it back rather than
        // assuming our preferred mimeType above is what we got.
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mimeType || 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setRecordingState('recorded');
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecordingState('recording');

      // Drive the elapsed counter off a start timestamp (no setInterval drift) and hard-stop at the cap.
      setRecordingSeconds(0);
      recordStartRef.current = Date.now();
      recordTimerRef.current = setInterval(() => {
        const elapsed = Math.min(
          Math.floor((Date.now() - recordStartRef.current) / 1000),
          MAX_RECORDING_SECONDS,
        );
        setRecordingSeconds(elapsed);
        if (elapsed >= MAX_RECORDING_SECONDS) stopRecording();
      }, 500);
    } catch (err) {
      console.error('Mic access error:', err);
      setSubmitError('Could not access your microphone. Check your browser permissions and try again.');
    }
  };

  const stopRecording = () => {
    clearRecordTimers();
    mediaRecorderRef.current?.stop();
  };

  const reRecord = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingState('idle');
    setRecordingSeconds(0);
    setSubmitError('');

    const audio = document.getElementById('user-recording');
    audio.pause();
    audio.removeAttribute('src');
    audio.innerHTML = '';
    audio.load();
  };

  const handleRecordButtonClick = () => {
    if (recordingState === 'idle') startRecording();
    else if (recordingState === 'recording') stopRecording();
  };

  const handleSubmitAnswer = async () => {
    if (!audioBlob || !currentQuestion) return;
    setRecordingState('uploading');
    setSubmitError('');
    try {
      await submitResponseAudio(currentQuestion.responseId, audioBlob);

      // fully reset the <audio> element so it drops the old blob src
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      const audio = document.getElementById('user-recording');
      audio.pause();
      audio.removeAttribute('src');
      audio.innerHTML = '';
      audio.load();

      if (isLastQuestion) {
        await endSession(sessionId);
        setFinished(true);
      } else {
        setCurrentIndex((i) => i + 1);
        setAudioBlob(null);
        setAudioUrl(null);
        setRecordingState('idle');
      }
    } catch (err) {
      console.error('Submit answer error:', err);
      setSubmitError('Could not submit your answer. Try again.');
      setRecordingState('recorded');
    }
  };

  const handleProceed = async () => {
    if (proceeding) return;
    setSubmitError('');
    if (isLastQuestion) {
      setProceeding(true);
      try {
        await endSession(sessionId);
        setFinished(true);
      } catch (err) {
        console.error('End session error:', err);
        setSubmitError('Could not finish the session. Try again.');
        setProceeding(false);
      }
      return;
    }
    setCurrentIndex((i) => i + 1);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingState('idle');
  };

  const handleSkip = async () => {
    if (skipping || !currentQuestion) return;
    setSubmitError('');
    setSkipping(true);
    try {
      await skipResponse(currentQuestion.responseId);

      // stop any in-progress recording before moving on
      clearRecordTimers();
      mediaRecorderRef.current?.state === 'recording' && mediaRecorderRef.current.stop();
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      if (audioUrl) URL.revokeObjectURL(audioUrl);

      if (isLastQuestion) {
        await endSession(sessionId);
        setFinished(true);
        return;
      }
      setCurrentIndex((i) => i + 1);
      setAudioBlob(null);
      setAudioUrl(null);
      setRecordingState('idle');
    } catch (err) {
      console.error('Skip question error:', err);
      setSubmitError(isLastQuestion ? 'Could not skip and end the session. Try again.' : 'Could not skip this question. Try again.');
    } finally {
      setSkipping(false);
    }
  };

  useEffect(() => {
    return () => {
      clearRecordTimers();
      streamRef.current?.getTracks().forEach((track) => track.stop());
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      questionAudioRef.current?.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bgClass = isDarkMode ? 'bg-surface text-slate-100' : 'bg-surface text-slate-900';

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${bgClass}`}>
        <div className="text-center">
          <Loader2 size={40} className="animate-spin mx-auto mb-4 text-blue-500" />
          <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Loading your interview session...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${bgClass}`}>
        <div className="text-center max-w-sm px-6">
          <p className="text-red-500 font-semibold mb-2">Something went wrong</p>
          <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>{loadError}</p>
        </div>
      </div>
    );
  }

  if (finished) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${bgClass}`}>
        <div className="text-center max-w-sm px-6">
          <CheckCircle size={48} className="text-primary-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Interview complete</h2>
          <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
            Your answers are being analyzed. You can close this tab — results will show up in your Interview History.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col items-center px-6 py-10 ${bgClass}`}>
      <div className="w-full max-w-xl">
        <div className={`rounded-2xl border mb-6 ${
          isDarkMode ? 'bg-slate-800/60 border-slate-700' : 'bg-slate-100 border-slate-200'
        }`}>
          <button
            type="button"
            onClick={() => setTipsOpen((o) => !o)}
            aria-expanded={tipsOpen}
            className={`w-full flex items-center justify-between gap-2 p-4 text-sm font-bold ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}
          >
            <span className="flex items-center gap-2">
              <Info size={16} />
              Things to keep in mind
            </span>
            {tipsOpen
              ? <X size={16} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />
              : <ChevronDown size={16} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />}
          </button>
          <div className={`grid transition-all duration-500 ease-in-out ${tipsOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
            <div className="overflow-hidden">
              <ol className={`list-decimal pl-9 pr-4 pb-4 space-y-1.5 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                <li>You only have <strong className={isDarkMode ? 'text-slate-100' : 'text-slate-900'}>3 minutes</strong> to answer the question, the recording will automatically stop when your response reached the limit.</li>
                <li>You can re-record a response for a question as many times as you want.</li>
                <li>But once you press submit or skip, you <strong className={isDarkMode ? 'text-slate-100' : 'text-slate-900'}>cannot</strong> go back to the previous question — it&apos;s a one-way process.</li>
                <li>If you accidentally close this page, don&apos;t worry — your submitted response is saved and you can continue by reopening the session from the History tab.</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <span className={`text-sm font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Question {currentIndex + 1} of {questions.length}
          </span>
          <div className="flex gap-1.5">
            {questions.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 w-8 rounded-full ${
                  i < currentIndex ? 'bg-primary-500' : i === currentIndex ? 'bg-primary-700' : (isDarkMode ? 'bg-slate-700' : 'bg-slate-200')
                }`}
              />
            ))}
          </div>
        </div>

        <div className={`rounded-2xl border p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <CategoryBadge category={currentQuestion?.category} isDarkMode={isDarkMode} />
            {!alreadyAnswered && (
              <button
                type="button"
                onClick={handleSkip}
                disabled={skipping || proceeding || recordingState === 'recording' || recordingState === 'uploading'}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50 ${
                  isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                }`}
              >
                {skipping
                  ? <Loader2 size={14} className="animate-spin" />
                  : <SkipForward size={14} />}
                {skipping
                  ? (isLastQuestion ? 'Ending...' : 'Skipping...')
                  : (isLastQuestion ? 'Skip and End Session' : 'Skip')}
              </button>
            )}
          </div>
          <p className="text-lg font-medium leading-relaxed mb-4">{currentQuestion?.text}</p>
          <button
            type="button"
            onClick={speakQuestion}
            disabled={isSpeaking}
            className={`inline-flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors ${
              isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-200' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
            } disabled:opacity-50`}
          >
            <Volume2 size={16} className={isSpeaking ? 'animate-pulse' : ''} />
            {isSpeaking ? 'Reading question...' : 'Replay question'}
          </button>
        </div>
        {!alreadyAnswered && (
          <div className={`flex items-center justify-center mt-2 mb-8`}>
            <p className={`text-md ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Listen to the question and record your answer by pressing the button below</p>
          </div>
        )}

        {alreadyAnswered ? (
          <div className="flex flex-col items-center gap-4 mt-8">
            <div className={`inline-flex items-center gap-2 text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              <CheckCircle size={18} className="text-primary-500" />
              You already answered this question
            </div>
            <button
              type="button"
              onClick={handleProceed}
              disabled={proceeding}
              className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white disabled:opacity-50"
            >
              {proceeding
                ? (isLastQuestion ? 'Finishing...' : 'Loading...')
                : (isLastQuestion ? 'Finish' : 'Proceed')}
              <ArrowRight size={16} />
            </button>
            {submitError && <p className="text-sm text-red-500 text-center">{submitError}</p>}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <RecordButton state={recordingState} onClick={handleRecordButtonClick} disabled={false} />
            <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              {recordingState === 'idle' && 'Press to start recording your answer (max 3 minutes)'}
              {recordingState === 'recording' && 'Recording... press to stop'}
              {recordingState === 'recorded' && `Recorded (${formatDuration(recordingSeconds)}) — review below, or submit`}
              {recordingState === 'uploading' && 'Submitting your answer...'}
            </p>
            {recordingState === 'recording' && (
              <p className={`text-sm font-mono tabular-nums ${
                recordingSeconds >= MAX_RECORDING_SECONDS - 30
                  ? 'text-red-500'
                  : (isDarkMode ? 'text-slate-400' : 'text-slate-500')
              }`}>
                {formatDuration(recordingSeconds)} / {formatDuration(MAX_RECORDING_SECONDS)}
                {recordingSeconds >= MAX_RECORDING_SECONDS && ' — time limit reached'}
              </p>
            )}

            <div className={`w-full flex flex-col items-center gap-3 ${recordingState === 'recorded' ? "" : "opacity-50"}`}>
              <audio id="user-recording" controls src={recordingState === 'idle' ? null : audioUrl} className="w-full max-w-xs" />
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={reRecord}
                  disabled={(recordingState === 'recorded' && audioUrl) ? false : true}
                  className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg ${
                    isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-200' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                  }`}
                >
                  <RotateCcw size={16} />
                  Re-record
                </button>
                <button
                  type="button"
                  onClick={handleSubmitAnswer}
                  disabled={(recordingState === 'recorded' && audioUrl) ? false : true}
                  className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white"
                >
                  {isLastQuestion ? 'Submit & Finish' : 'Submit & Continue'}
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>

            {submitError && <p className="text-sm text-red-500 text-center">{submitError}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
